import React from 'react';
import PropTypes from 'prop-types';
import { useLabels } from 'wsm-common-data';
import { setClassNames } from 'ddc-classnames-js';
import DealerResponse from './DealerResponse';

/**
 * Review component
 * @component
 * @param {object} props - Props for the component.
 * @param {Review} props.review - The review object.
 * @returns {React.Element} - Rendered component.
 */
const Review = ({ review, ...delegatedProps }) => {
	const labels = useLabels();
	const ratingStyle = {
		'--rating-star-value': review.commentRating
	};
	const showDealerResponse = Boolean(
		review.dealerResponse?.dealerResponseText?.length > 0
	);
	const showCommentText = review.commentText !== 'No comments provided';
	const typeModelYearRowClasses = [
		showCommentText ? 'mb-0' : '',
		'review__type-model-year',
		'd-flex',
		'mb-0'
	];
	// CSS classes
	const reviewClasses = [
		'review',
		'card',
		'between-dotted-underline',
		delegatedProps.className ? delegatedProps.className : ''
	];

	return (
		<li aria-label="review" className={setClassNames(reviewClasses)}>
			<article role="contentinfo">
				<p className="review__name-date-rating d-flex mb-0">
					<span
						className="font-weight-bold"
						aria-label={review.reviewer.displayName}
					>
						{review.reviewer.displayName}
					</span>
					{review.commentDate}
					<span className="ml-auto rating-container">
						<span
							className="rating-stars"
							style={ratingStyle}
							aria-label={`${labels.get(
								'VW_REVIEWS_FILTER_RATING'
							)}: ${review.commentRating}`}
						/>
					</span>
				</p>
				<p className={setClassNames(typeModelYearRowClasses)}>
					<span className="review__info">
						<span className="font-weight-bold pr-2">
							{labels.get('VW_REVIEWS_TYPE')}:
						</span>
						{review.categoryKey}
					</span>
					<span className="review__info">
						<span className="font-weight-bold pr-2">
							{labels.get('VW_REVIEWS_MODEL')}:
						</span>
						{review.model}
					</span>
					<span className="review__info">
						<span className="font-weight-bold pr-2">
							{labels.get('VW_REVIEWS_MODELYEAR')}:
						</span>
						{review.modelYear}
					</span>
				</p>
				{showCommentText && (
					<p className="p-0 pt-3">{review.commentText}</p>
				)}
			</article>
			{showDealerResponse && (
				<DealerResponse date={review.dealerResponse.dealerResponseDate}>
					{review.dealerResponse.dealerResponseText}
				</DealerResponse>
			)}
		</li>
	);
};

Review.propTypes = {
	review: PropTypes.shape({
		reviewer: PropTypes.shape({
			displayName: PropTypes.string
		}),
		commentID: PropTypes.string,
		categoryKey: PropTypes.string,
		dealerID: PropTypes.string,
		commentRating: PropTypes.string,
		commentText: PropTypes.string,
		commentDate: PropTypes.string,
		modelYear: PropTypes.string,
		model: PropTypes.string,
		dealerResponse: PropTypes.shape({
			dealerResponseText: PropTypes.string,
			dealerResponseDate: PropTypes.string
		})
	}).isRequired
};

export default Review;
